// The monthly download allowance (#764), as the web sees it.
//
// The contract is docs/download-metering.md. The month is the calendar month
// in Asia/Singapore and resets on the 1st at 00:00 SGT. Singapore has kept
// UTC+8 with no daylight saving since 1982, so a fixed offset is exact and
// needs no timezone database.
//
// Deliberately absent: the enforcement point. Downloads are suspended at 110%
// of the documented allowance, and that 10% grace is never shown to a
// customer, never computed here and never rendered. The system-admin panel
// shows the figure the API sends it; nothing in this file derives it.

const SGT_OFFSET_MS = 8 * 60 * 60 * 1000;
const TIMEZONE = 'Asia/Singapore';

/** The allowance month a moment falls in: "YYYY-MM" in Asia/Singapore. */
export function sgtMonth(now = new Date()) {
  const shifted = new Date(now.getTime() + SGT_OFFSET_MS);
  const month = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  return `${shifted.getUTCFullYear()}-${month}`;
}

/** The next 1st of the month, 00:00 SGT, as a Date — when the allowance resets. */
export function nextResetAt(now = new Date()) {
  const shifted = new Date(now.getTime() + SGT_OFFSET_MS);
  return new Date(Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, 1) - SGT_OFFSET_MS);
}

/**
 * Is this workspace's downloading suspended right now? Only when the flag is
 * set *and* it belongs to the current month: a flag left over from last month
 * is harmless even before the reset job clears it.
 */
export function isDownloadsSuspended(subscriber, now = new Date()) {
  return subscriber?.downloads_suspended === true
    && subscriber?.downloads_suspended_month === sgtMonth(now);
}

/** The current allowance month and the `count - 1` before it, newest first. */
export function recentMonths(count = 12, now = new Date()) {
  const [year, month] = sgtMonth(now).split('-').map(Number);
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(Date.UTC(year, month - 1 - i, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  });
}

/** "2026-09" -> "September 2026". */
export function monthLabel(month) {
  const [year, m] = String(month || '').split('-').map(Number);
  if (!year || !m) return month || '';
  return new Date(Date.UTC(year, m - 1, 1)).toLocaleDateString('en-GB', {
    month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

/** A reset moment (Date or ISO string) as the date it falls on in Singapore: "1 October 2026". */
export function formatResetDate(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: TIMEZONE,
  });
}

/**
 * A timestamp from the API as a Date: an ISO string, epoch millis, or a
 * serialised Firestore timestamp (`{seconds}` / `{_seconds}`). Null otherwise.
 */
export function toDateValue(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  const seconds = value.seconds ?? value._seconds;
  const date = typeof seconds === 'number' ? new Date(seconds * 1000) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** The one sentence every suspended control and banner uses. */
export function suspendedMessage(resetAt) {
  const date = formatResetDate(resetAt);
  return `This month's download allowance is used up. Downloads resume on ${date || 'the 1st of next month'}.`;
}

/**
 * An Error for a failed API response carrying the API's own `detail` and the
 * status. A 429 is the download allowance talking, and its detail names the
 * allowance and the reset date — far more use than "request failed".
 */
export async function responseError(response, fallback) {
  let detail = '';
  try {
    const body = await response.json();
    detail = typeof body?.detail === 'string' ? body.detail : '';
  } catch {
    detail = '';
  }
  const error = new Error(detail || `${fallback} (${response.status})`);
  error.status = response.status;
  return error;
}

export const DOWNLOAD_CHANNELS = ['storage', 'api', 'onedrive', 'email', 'webhook', 'drive'];
export const DOWNLOAD_KINDS = ['fullres', 'preview', 'export', 'attachment', 'other'];

export const CHANNEL_LABELS = {
  storage: 'Files and photos',
  api: 'Reports and exports',
  onedrive: 'OneDrive sync',
  email: 'Email attachments',
  webhook: 'Webhooks',
  drive: 'Google Drive sync',
};

export const KIND_LABELS = {
  fullres: 'Full-resolution photos and video',
  // Thumbnails, and full-size photos and clips shown in the web viewer or on
  // the phone. Only an explicit download counts (#764).
  preview: 'Viewing in the app (not counted)',
  export: 'Exports and reports',
  attachment: 'Attachments',
  other: 'Other',
};

/** Channels and kinds whose bytes never count toward the allowance. */
export const UNCOUNTED_CHANNELS = ['drive'];
export const UNCOUNTED_KINDS = ['preview'];

/**
 * The kinds a workspace is shown. Viewing in the app (`preview`) is not one:
 * it is not a download, and since 2026-10-11 it is not recorded either
 * (product owner). Months from before that still hold views; they are left
 * out of what a workspace sees.
 */
export const LISTED_KINDS = DOWNLOAD_KINDS.filter((kind) => !UNCOUNTED_KINDS.includes(kind));
